import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { useState, useEffect, useMemo, useRef } from "react";
import { Input } from "@/components/Input";
import { FormSubmit } from "@/components/FormSubmit";
import { SelectRoot } from "@/components/Select";
import { LocationSelect } from "@/components/LocationSelect";
import { PhotoUpload } from "@/components/PhotoUpload";
import { MasterDataItem, useMasterData } from "@/hooks/useMasterData";
import { MASTER_SCOPES } from "@/constants/masterScopes";
import { api, ExperienceEnum } from "@/services/api";
import { useExpertSignupPrefill } from "@/hooks/useExpertSignupPrefill";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/useToast";
import { INVALID_ORG_CODE, PROFILE_SAVE_ERROR } from "@/utils/toastMessages";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { parseMasterData } from "@/utils/util";
import { profileStepCompleted } from "@/utils/tracking";
import { useAuth } from "@/hooks/useAuth";
import { getPrimaryRole, hasRole } from "@/utils/roleUtils";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { ACHIEVER_EXPERIENCES } from "@/constants/experiences";
import { CheckCircleFilledIcon, ArrowRightIcon } from "@/components/icons/icons";
import { FieldLabel } from "@/components/FieldLabel";
import { Textarea } from "@/components/Textarea";
import { BIO_MAX_LENGTH, BIO_MIN_LENGTH } from "@/pages/profile/components/bioCard";

const WEBSITE_RE = /^(https?:\/\/)?[^\s/]+\.[^\s]+$/i;

// Field set differs by context: the base signup form (!embedded) has no
// Name/Email fields (already collected at account-creation step 1); the
// Expert Application wizard's "Basic Profile" step (embedded) recaps them.
//
// Website and Bio are optional here and made required for Experts by
// `withExpertRules` below — kept as a plain object so handleApiError can still
// read its shape for 422 field labels.
const buildProfileSchema = (embedded: boolean) =>
  z.object({
    firstName: embedded ? z.string().trim().min(1, "Please enter your First Name") : z.string(),
    lastName: embedded ? z.string().trim().min(1, "Please enter your Last Name") : z.string(),
    currentCompany: z.string().trim().min(1, "Please enter your Company Name"),
    // Single values from profile::job-role / profile::industry.
    jobRole: z.string().trim().min(1, "Please select your Job Role"),
    industry: z.string().trim().min(1, "Please select your Industry"),
    currentTitle: z.string().trim().min(1, "Please enter your Current Job Title"),
    currentRoleLevel: z.string().trim().min(1, "Please select your Current Role Level"),
    primaryFunction: z.string().trim().min(1, "Please select your Primary Operator Function"),
    yearsOfExperience: z.string().trim().min(1, "Please select your Years of Experience"),
    linkedIn: z
      .string()
      .trim()
      .min(1, "Please enter your LinkedIn URL")
      .refine((v) => /linkedin\.com\//i.test(v), "Please enter a LinkedIn profile URL (linkedin.com/in/…)"),
    personalWebsite: z
      .string()
      .trim()
      .refine((v) => !v || WEBSITE_RE.test(v), "Please enter a valid website address"),
    // Experts only (see showBio). An achiever's bio is written on signup step
    // 3; it is loaded here only so saving this step doesn't wipe it.
    bio: z.string().trim(),
    stateRegion: z.string().trim().min(1, "Please enter your State / Region"),
    // Optional -- only shown to LinkedIn signups (see showOrgCodeField).
    organizationCode: z.string().optional(),
  });

type ProfileFormInputs = z.infer<ReturnType<typeof buildProfileSchema>>;

/**
 * Experts are presented to achievers on the strength of this profile, so for
 * them the website/portfolio and a bio are required. Achievers may skip the
 * website, and don't get the bio field here at all.
 */
const withExpertRules = (
  schema: ReturnType<typeof buildProfileSchema>,
  isExpert: () => boolean,
) =>
  schema.superRefine((data, ctx) => {
    if (!isExpert()) return;
    if (!data.personalWebsite) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["personalWebsite"],
        message: "Please enter your Personal Website / Portfolio",
      });
    }
    if (!data.bio) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bio"], message: "Please add a short bio" });
    } else if (data.bio.length < BIO_MIN_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bio"],
        message: `Please write at least ${BIO_MIN_LENGTH} characters (${data.bio.length} so far)`,
      });
    } else if (data.bio.length > BIO_MAX_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bio"],
        message: `Please keep your bio to ${BIO_MAX_LENGTH} characters`,
      });
    }
  });

interface UserProfileFormProps {
  joinProgramId?: string;
  /** When true, hides the internal heading/photo-upload/submit button — for embedding inside another wizard shell (e.g. the Expert Application wizard) that supplies its own heading and navigation. */
  embedded?: boolean;
  /** DOM id applied to the <form> so an external button (e.g. a wizard's Continue button) can trigger submit when embedded. */
  formId?: string;
  /** Called after a successful save when embedded, instead of navigating onward. */
  onSubmitSuccess?: () => void;
  /** Reports loading state to the parent when embedded, since the submit button lives outside this form. */
  onLoadingChange?: (loading: boolean) => void;
  /** Signup step 2 only: lets LinkedIn achievers enter the Group / Company Code
   *  they never got to type on step 1. Off everywhere else (e.g. the Expert
   *  Application), so the field can't appear outside signup. */
  allowOrgCode?: boolean;
}

/**
 * Step 2 ("Build Your Profile") — shared by both achiever and Expert
 * (advisor) signup. Both roles collect the exact same shared professional
 * profile fields and write to the same user_profiles row, so both submit
 * through the achiever profile endpoints (api.achievers.*) regardless of
 * the signed-up role. Advisors additionally get their profile auto-filled
 * from CRM lead data, same as the old advisor-only form did.
 */
export const UserProfileForm = ({
  embedded = false,
  formId,
  onSubmitSuccess,
  onLoadingChange,
  allowOrgCode = false,
}: UserProfileFormProps) => {
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [profileExists, setProfileExists] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | undefined>(undefined);
  // No longer edited here (Industry replaced it), but the advisor profile PUT
  // requires the field -- re-send what was loaded so it isn't wiped.
  const [existingExperienceContext, setExistingExperienceContext] = useState<string[]>([]);
  // Only surfaced after a submit attempt, so the form doesn't greet the user
  // with an error before they've had a chance to upload anything.
  const [photoError, setPhotoError] = useState(false);
  const photoSectionRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { user, refreshUser } = useAuth();
  const isAdvisor = !!user && hasRole(user, "advisor");
  // Who this profile is being filled in AS. An Expert signup, or the Expert
  // Application wizard (embedded) — the applicant there is still an achiever
  // by role, but is building the profile achievers will see them by. Experts
  // must add a photo, a website/portfolio and a bio; achievers may skip them.
  const isExpert = isAdvisor || embedded;
  const isExpertRef = useRef(isExpert);
  isExpertRef.current = isExpert;
  // LinkedIn signup skips the step-1 email form, which is where the Group /
  // Company Code is normally entered, so LinkedIn users get it here instead.
  // Achiever-only, same as step 1 (advisors never send a code).
  const showOrgCodeField = allowOrgCode && user?.authProvider === "linkedin" && !isAdvisor;
  // The backend stores "ORG-TEAM" codes split across two columns.
  const existingOrgCode = user?.organizationCode
    ? user.teamCode
      ? `${user.organizationCode}-${user.teamCode}`
      : user.organizationCode
    : "";

  // Built once per `embedded` value so the same instance backs both the
  // resolver and handleApiError's 422 field-label lookup. The Expert rules
  // read the role through a ref, because the user can arrive after the
  // resolver has been handed to useForm.
  const profileSchema = useMemo(() => buildProfileSchema(embedded), [embedded]);
  const resolverSchema = useMemo(
    () => withExpertRules(profileSchema, () => isExpertRef.current),
    [profileSchema],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm<ProfileFormInputs>({
    resolver: zodResolver(resolverSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    // scrollToFirstError picks the first error in page order (photo and
    // selects included); RHF's own focus only reaches register()ed inputs.
    shouldFocusError: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      currentCompany: "",
      jobRole: "",
      industry: "",
      currentTitle: "",
      currentRoleLevel: "",
      primaryFunction: "",
      yearsOfExperience: "",
      linkedIn: "",
      personalWebsite: "",
      bio: "",
      stateRegion: "",
      organizationCode: "",
    },
  });

  const { showToast } = useToast();
  const handleApiError = useErrorHandler();
  const navigate = useNavigate();
  const { updateStep } = useUpdateOnboardingStep();

  const { data: jobRoles } = useMasterData<MasterDataItem>(MASTER_SCOPES.JOB_ROLE);
  const { data: industries } = useMasterData<MasterDataItem>(MASTER_SCOPES.PROFILE_INDUSTRY);
  const { data: primaryFunctions } = useMasterData<MasterDataItem>(MASTER_SCOPES.PRIMARY_FUNCTION);
  const { data: roleLevels } = useMasterData<MasterDataItem>(MASTER_SCOPES.ROLE_LEVEL);

  // Experts get steps 2-4 prefilled from their CRM record (if the CRM has one).
  // Applied once the saved profile has loaded, and only into fields that are
  // still empty -- never over something the user saved or typed.
  const [profileLoaded, setProfileLoaded] = useState(false);
  const crmAppliedRef = useRef(false);
  const { data: crmPrefill } = useExpertSignupPrefill(isExpert);

  useEffect(() => {
    if (!profileLoaded || !crmPrefill || crmAppliedRef.current) return;
    crmAppliedRef.current = true;

    const fill = (field: keyof ProfileFormInputs, value: string | null | undefined) => {
      if (!value || getValues(field)) return false;
      setValue(field, value, { shouldDirty: true });
      return true;
    };
    const filled = [
      embedded && fill("firstName", crmPrefill.firstName),
      embedded && fill("lastName", crmPrefill.lastName),
      fill("currentTitle", crmPrefill.jobTitle),
      fill("currentCompany", crmPrefill.companyName),
      fill("jobRole", crmPrefill.jobRole),
      fill("industry", crmPrefill.industry),
      fill("stateRegion", crmPrefill.location),
      fill("yearsOfExperience", crmPrefill.yearsOfExperience),
      fill("primaryFunction", crmPrefill.primaryFunction),
      fill("currentRoleLevel", crmPrefill.currentRoleLevel),
      fill("linkedIn", crmPrefill.linkedIn),
      fill("personalWebsite", crmPrefill.personalWebsite),
    ].some(Boolean);

    if (filled) {
      showToast({
        type: "info",
        message: "We've pre-filled some details from your Expert application. Please review them.",
      });
    }
  }, [profileLoaded, crmPrefill, embedded, getValues, setValue, showToast]);

  useEffect(() => {
    // Resolve identity from the already-authenticated user, not GET /achievers/me --
    // that endpoint 404s for non-achiever roles (e.g. advisors), even though the
    // profile read/write endpoints below work for any role.
    if (!user) return;
    setUserId(user.id);

    // First/Last Name aren't stored separately -- split from the single
    // full_name column on the User record (recombined on submit).
    if (user.fullName) {
      const [first, ...rest] = user.fullName.trim().split(/\s+/);
      setValue("firstName", first || "");
      setValue("lastName", rest.join(" "));
    }
    if (existingOrgCode) setValue("organizationCode", existingOrgCode);

    const fetchExistingProfile = async () => {
      try {
        // The achiever profile-write/read permissions aren't granted to the advisor
        // role (confirmed against role_feature_map) -- advisors have their own
        // advisors:profile:* permissions and must go through api.advisors.* instead.
        const existing = (
          isAdvisor
            ? await api.advisors.getProfile(user.id)
            : await api.achievers.getProfile(user.id)
        ) as Record<string, unknown>;
        if (existing && Object.keys(existing).length > 0) {
          setProfileExists(true);
          if (existing.currentCompany)
            setValue("currentCompany", existing.currentCompany as string);
          if (Array.isArray(existing.experienceContext)) {
            setExistingExperienceContext(existing.experienceContext as string[]);
          }
          if (existing.jobRole) setValue("jobRole", existing.jobRole as string);
          if (existing.industry) setValue("industry", existing.industry as string);
          if (existing.currentTitle) setValue("currentTitle", existing.currentTitle as string);
          if (existing.currentRoleLevel)
            setValue("currentRoleLevel", existing.currentRoleLevel as string);
          if (existing.primaryFunction)
            setValue("primaryFunction", existing.primaryFunction as string);
          if (existing.yearsOfExperience)
            setValue("yearsOfExperience", existing.yearsOfExperience as string);
          if (existing.linkedIn) setValue("linkedIn", existing.linkedIn as string);
          if (existing.personalWebsite)
            setValue("personalWebsite", existing.personalWebsite as string);
          if (typeof existing.bio === "string" && existing.bio) setValue("bio", existing.bio);
          if (existing.location && typeof existing.location === "string") {
            setValue("stateRegion", existing.location as string);
          }
          if (existing.profilePictureUrl) {
            setProfilePictureUrl(existing.profilePictureUrl as string);
          }
        }
      } catch {
        // No existing profile — first time creation, fall through to CRM prefill
      } finally {
        setProfileLoaded(true);
      }
    };
    fetchExistingProfile();
  }, [user, isAdvisor, existingOrgCode, setValue]);

  const bioValue = watch("bio") || "";

  const handleImageSelect = (file: File, previewUrl: string) => {
    setPendingImageFile(file);
    setProfilePictureUrl(previewUrl);
    setPhotoError(false);
  };

  // Experts only — achievers may continue without a photo.
  const photoMissing = isExpert && !profilePictureUrl && !pendingImageFile;

  // Field errors from zod. The photo isn't a zod field, so flag it in the same
  // pass — before, it was only checked once every other field was valid —
  // then take the user to the first problem on the page.
  const onInvalid: SubmitErrorHandler<ProfileFormInputs> = () => {
    if (photoMissing) setPhotoError(true);
    scrollToFirstError(formRef.current);
  };

  const onsubmit: SubmitHandler<ProfileFormInputs> = async (data) => {
    // An Expert's profile photo is required, but it isn't a zod field (the
    // file lives in component state, not the form payload), so the resolver
    // can't enforce it -- this guard is what actually blocks submission for
    // both the standalone submit button and the embedded wizard's external
    // Continue button, since both dispatch through handleSubmit(onsubmit).
    if (photoMissing) {
      setPhotoError(true);
      scrollToFirstError(formRef.current);
      return;
    }
    setPhotoError(false);

    setLoading(true);
    onLoadingChange?.(true);
    try {
      const id = userId ?? user?.id;
      if (!id) {
        throw new Error("Unable to resolve the current user's id");
      }

      const sharedProfileFields = {
        currentTitle: data.currentTitle,
        currentCompany: data.currentCompany,
        location: data.stateRegion,
        yearsOfExperience: data.yearsOfExperience as ExperienceEnum,
        primaryFunction: data.primaryFunction,
        organisationType: [] as string[],
        currentRoleLevel: data.currentRoleLevel,
        // Always the loaded/entered value. This used to send "", which wiped
        // an achiever's step-3 bio whenever they came back and saved step 2.
        bio: data.bio,
        linkedIn: data.linkedIn,
        personalWebsite: data.personalWebsite,
        experienceContext: existingExperienceContext,
        jobRole: data.jobRole,
        industry: data.industry,
      };

      // First/Last Name aren't part of user_profiles -- they combine back into
      // the User record's single full_name column via the shared profile-update
      // endpoint, independently of the achiever/advisor profile write below.
      const fullName = `${data.firstName} ${data.lastName}`.trim();
      // Only send the code when it changed -- sending "" clears the user's
      // company, which would drop a code that came in via a ?code= link.
      const orgCode = (data.organizationCode || "").trim();
      const orgCodeChanged = showOrgCodeField && orgCode !== existingOrgCode;
      const nameChanged = !!fullName && fullName !== user?.fullName;
      // The code is optional (empty is fine), but a code that was typed must be
      // valid: an unknown code is a 400, so stop before anything else is saved.
      if (orgCodeChanged && orgCode) {
        try {
          await api.users.updateProfile({ organizationCode: orgCode });
        } catch (err) {
          if (err instanceof AxiosError && err.response?.status === 400) {
            showToast({ type: "error", message: INVALID_ORG_CODE });
            return;
          }
          throw err;
        }
      } else if (orgCodeChanged) {
        // Field was cleared -- remove the previous code.
        await api.users.updateProfile({ organizationCode: "" });
      }
      if (nameChanged) {
        await api.users.updateProfile({ fullName });
      }
      if (nameChanged || orgCodeChanged) {
        await refreshUser();
      }

      // Same permission split as the hydration fetch above -- advisors don't hold
      // any achievers:profile:write* permission, so writes must go through
      // api.advisors.* for that role. goalsAndChallenges/topGrowthAreas aren't
      // collected by this shared form (always sent empty) and aren't part of the
      // advisor profile schema, so they're achiever-only additions below.
      if (isAdvisor) {
        if (pendingImageFile) {
          await api.advisors.updateProfileWithPreferences(sharedProfileFields, pendingImageFile);
          setProfileExists(true);
        } else if (profileExists) {
          await api.advisors.updateProfile(id, sharedProfileFields);
        } else {
          await api.advisors.createProfile(id, sharedProfileFields);
          setProfileExists(true);
        }
      } else {
        const achieverProfilePayload = {
          ...sharedProfileFields,
          topGrowthAreas: [] as string[],
          goalsAndChallenges: "",
        };
        if (pendingImageFile) {
          await api.achievers.updateProfileWithPreferences(
            achieverProfilePayload,
            pendingImageFile,
          );
          setProfileExists(true);
        } else if (profileExists) {
          await api.achievers.updateProfile(id, achieverProfilePayload);
        } else {
          await api.achievers.createProfile(id, achieverProfilePayload);
          setProfileExists(true);
        }
      }

      if (user) {
        profileStepCompleted({ step_name: "profile_completed", user_role: getPrimaryRole(user) });
      }

      // v3: every signup flow walks all 3 steps -- invited, organic, and
      // public "/join/:slug" alike. The join paths used to collapse to 2 by
      // skipping signup/3 straight to complete; they no longer do, so program
      // members finish with the same profile data as anyone else.
      if (embedded) {
        onSubmitSuccess?.();
      } else {
        await updateStep("signup/3");
        navigate("/signup/3");
      }
    } catch (err: unknown) {
      handleApiError(err, profileSchema);
      if (!(err as Record<string, unknown>)?.isAxiosError) {
        showToast({ type: "error", message: PROFILE_SAVE_ERROR });
      }
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <form
      ref={formRef}
      id={formId}
      onSubmit={handleSubmit(onsubmit, onInvalid)}
      className="flex flex-col gap-6"
    >
      {/* Heading */}
      {!embedded && (
        <div className="flex flex-col gap-2">
          <h1 className="text-[28px] font-bold leading-tight tracking-[0.3px] text-theme-text-heading">
            {profileExists ? "Update Your Profile" : "Build Your Profile"}
          </h1>
          <p className="text-18 text-theme-text-muted leading-7 tracking-[-0.44px]">
            Tell us about your professional background
          </p>
          {user?.email && (
            <div className="flex items-center gap-1.5">
              <CheckCircleFilledIcon className="text-theme-text-success" />
              <span className="text-12 font-medium text-theme-text-success">
                Signed in as {user.email}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Group / Company Code -- LinkedIn signups only (same field as step 1) */}
      {showOrgCodeField && (
        <Input
          id="organizationCode"
          label="Group / Company Code"
          optional
          placeholder="e.g., ACME-2026"
          hint="If your company has a Forward plan, enter your code for group pricing."
          error={errors.organizationCode?.message}
          {...register("organizationCode")}
        />
      )}

      {/* Row: First Name + Last Name -- Expert Application recap only; the
          base signup form already collected these at account-creation. */}
      {embedded && (
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <FieldLabel required htmlFor="firstName">
              First Name
            </FieldLabel>
            <Input
              id="firstName"
              {...register("firstName")}
              placeholder="e.g., Alex"
              error={errors.firstName?.message}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel required htmlFor="lastName">
              Last Name
            </FieldLabel>
            <Input
              id="lastName"
              {...register("lastName")}
              placeholder="e.g., Mollen"
              error={errors.lastName?.message}
            />
          </div>
        </div>
      )}

      {/* Row: Email (read-only) -- Expert Application recap only. */}
      {embedded && (
        <div className="flex flex-col gap-2">
          {/* No asterisk: it's read-only, taken from the account. */}
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input id="email" value={user?.email ?? ""} disabled readOnly />
        </div>
      )}

      {/* Profile Photo */}
      <div data-field className="flex flex-col gap-2" ref={photoSectionRef}>
        <FieldLabel required={isExpert} optionalHint={isExpert ? undefined : "optional"}>
          Profile Photo
        </FieldLabel>
        <PhotoUpload
          currentImageUrl={profilePictureUrl}
          onImageSelect={handleImageSelect}
          size={64}
          hint="Recommended: 1000×1000 (1:1). JPG or PNG. Max 10MB."
        />
        {photoError && photoMissing && (
          <p data-field-error className="error-message">Please upload a profile photo</p>
        )}
      </div>

      {/* Row: Company Name + Current Job Title */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel required htmlFor="currentCompany">
            Company Name
          </FieldLabel>
          <Input
            id="currentCompany"
            {...register("currentCompany")}
            placeholder="e.g., Acme Corp"
            error={errors.currentCompany?.message}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel required htmlFor="currentTitle">
            Current Job Title
          </FieldLabel>
          <Input
            id="currentTitle"
            {...register("currentTitle")}
            placeholder="e.g., Product Manager"
            error={errors.currentTitle?.message}
          />
        </div>
      </div>

      {/* Row: Job Role + Industry */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel required>Job Role</FieldLabel>
          <SelectRoot
            name="jobRole"
            searchable
            searchPlaceholder="Search job roles…"
            value={watch("jobRole") || undefined}
            onValueChange={(value) => setValue("jobRole", value, { shouldValidate: true })}
            options={parseMasterData(jobRoles) || []}
            placeholder="Select job role"
          />
          {errors.jobRole && (
            <p data-field-error className="error-message">{errors.jobRole.message}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel required>Industry</FieldLabel>
          <SelectRoot
            name="industry"
            searchable
            searchPlaceholder="Search industries…"
            value={watch("industry") || undefined}
            onValueChange={(value) => setValue("industry", value, { shouldValidate: true })}
            options={parseMasterData(industries) || []}
            placeholder="Select industry"
          />
          {errors.industry && (
            <p data-field-error className="error-message">{errors.industry.message}</p>
          )}
        </div>
      </div>

      {/* Row: Current Role Level + State / Region */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel required>Current Role Level</FieldLabel>
          <SelectRoot
            name="currentRoleLevel"
            searchable
            searchPlaceholder="Search role levels…"
            value={watch("currentRoleLevel") || undefined}
            onValueChange={(value) => setValue("currentRoleLevel", value, { shouldValidate: true })}
            options={parseMasterData(roleLevels) || []}
            placeholder="Select role level"
          />
          {errors.currentRoleLevel && (
            <p data-field-error className="error-message">{errors.currentRoleLevel.message}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel required>State / Region</FieldLabel>
          <LocationSelect
            name="stateRegion"
            value={watch("stateRegion") || ""}
            onChange={(value) => setValue("stateRegion", value, { shouldValidate: true })}
            error={!!errors.stateRegion}
          />
          {errors.stateRegion && <p data-field-error className="error-message">{errors.stateRegion.message}</p>}
        </div>
      </div>

      {/* Row: Primary Operator Function + Years of Experience */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel required>Primary Operator Function</FieldLabel>
          <SelectRoot
            name="primaryFunction"
            searchable
            searchPlaceholder="Search functions…"
            value={watch("primaryFunction") || undefined}
            onValueChange={(value) => setValue("primaryFunction", value, { shouldValidate: true })}
            options={parseMasterData(primaryFunctions) || []}
            placeholder="Select primary function"
          />
          {errors.primaryFunction && (
            <p data-field-error className="error-message">{errors.primaryFunction.message}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel required>Years of Experience</FieldLabel>
          <SelectRoot
            name="yearsOfExperience"
            value={watch("yearsOfExperience") || undefined}
            onValueChange={(value) =>
              setValue("yearsOfExperience", value, { shouldValidate: true })
            }
            options={ACHIEVER_EXPERIENCES || []}
            placeholder="Select years of experience"
          />
          {errors.yearsOfExperience && (
            <p data-field-error className="error-message">{errors.yearsOfExperience.message}</p>
          )}
        </div>
      </div>

      {/* Row: LinkedIn URL + Personal Website / Portfolio */}
      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel required htmlFor="linkedIn">
            LinkedIn URL
          </FieldLabel>
          <Input
            id="linkedIn"
            {...register("linkedIn")}
            placeholder="https://linkedin.com/in/…"
            error={errors.linkedIn?.message}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FieldLabel
            required={isExpert}
            optionalHint={isExpert ? undefined : "optional"}
            htmlFor="personalWebsite"
          >
            Personal Website / Portfolio
          </FieldLabel>
          <Input
            id="personalWebsite"
            {...register("personalWebsite")}
            placeholder="https://…"
            error={errors.personalWebsite?.message}
          />
        </div>
      </div>

      {/* Bio -- Experts only. Same 150–300 character rule as the profile's
          Bio card, so it can be edited there later as-is. */}
      {isExpert && (
        <div data-field className="flex flex-col gap-2">
          <FieldLabel required htmlFor="bio">
            Bio
          </FieldLabel>
          <p className="-mt-1 text-12 leading-4 text-theme-text-secondary">
            A short introduction achievers will see on your Expert profile — who you are, what
            you&apos;ve done and how you help.
          </p>
          <Textarea
            id="bio"
            {...register("bio")}
            // Watched value too, so a bio loaded after mount sizes the
            // Textarea to fit (see AdvisorPreferenceForm's text answers).
            value={bioValue}
            richText
            rows={4}
            maxRows={10}
            placeholder="e.g. Two-time COO who has scaled B2B SaaS teams from 20 to 300. I help founders build the operating cadence that lets them stop firefighting…"
            error={errors.bio?.message}
          />
          <p className="text-right text-12 text-theme-text-muted">
            {bioValue.length}/{BIO_MAX_LENGTH}
          </p>
        </div>
      )}

      {/* Submit */}
      {!embedded && (
        <div className="pt-2">
          <FormSubmit
            isLoading={loading}
            disabled={loading}
            variant="primaryGradient"
            className="h-12 md:h-14 text-14 md:text-16 font-semibold rounded-[14px]"
            rightIcon={<ArrowRightIcon className="text-white" />}
          >
            {isAdvisor
              ? profileExists
                ? "Update & Continue to Operator DNA"
                : "Continue to Operator DNA"
              : profileExists
                ? "Update & Continue Growth"
                : "Continue to Growth"}
          </FormSubmit>
        </div>
      )}
    </form>
  );
};
